import React from 'react'
import PropTypes from 'prop-types'

import DatePicker from 'react-datepicker'
import moment from 'moment'
import { Form, Header, Modal } from 'semantic-ui-react'
import { compose } from 'recompose'

import Container from './ExtendedSearchModalContainer'
import Enhancer from './ExtendedSearchModalEnhancer'

import Button from 'Components/StandardButton'
import FormInputDropdown from 'Components/FormInputDropdown'
import FormInputText from 'Components/FormInputText'
import Permissions from 'Components/Permissions'

import * as options from './options'
import * as styles from './styles'

const ExtendedSearchModal = props => {
  const {
    advisorOptions,
    errors,
    form,
    handleIsMasterpiece,
    handleAdvisor,
    handleAvailability,
    handleDateReceiptMax,
    handleDateReceiptMin,
    handleDateReservationMax,
    handleDateReservationMin,
    handleDescription,
    handleRequested,
    handleReservationEvent,
    handleReservationUser,
    handleReset,
    handleSizeQmMax,
    handleSizeQmMin,
    handleSource,
    handleSubmit,
    open,
    reservationEventOptions,
    reservationUserOptions,
    toggle,
  } = props
  const {
    advisor,
    availability,
    dateReceipt,
    dateReservation,
    description,
    isMasterpiece,
    requested,
    reservationEvent,
    reservationUser,
    sizeQm,
    source,
  } = form

  return (
    <Modal onClose={toggle} open={open}>
      <Modal.Header>Extended Search Parameters</Modal.Header>
      <Modal.Content>
        <Form size="tiny">
          <Permissions
            permissions="rug.field.isMasterpiece"
            render={() => (
              <FormInputDropdown
                multiple={false}
                name="masterpiece"
                onChange={handleIsMasterpiece}
                options={options.isMasterpiece}
                text="Marked as Masterpiece"
                value={isMasterpiece}
              />
            )}
          />

          <Permissions
            permissions="rug.field.advisor"
            render={() => (
              <FormInputDropdown name="advisor" onChange={handleAdvisor} options={advisorOptions} text="Advisor" value={advisor} />
            )}
          />

          <Permissions
            permissions="rug.field.pictureSource"
            render={() => (
              <FormInputDropdown name="source" onChange={handleSource} options={options.source} text="Picture Source" value={source} />
            )}
          />

          <Permissions
            permissions="rug.field.description"
            render={() => (
              <FormInputText icon="align justify" name="description" onChange={handleDescription} text="Description" value={description} />
            )}
          />

          <Permissions
            permissions="rug.field.requests"
            render={() => (
              <FormInputDropdown
                multiple={false}
                name="requested"
                onChange={handleRequested}
                options={options.requested}
                search={false}
                text="Request Status"
                value={requested}
              />
            )}
          />

          <Permissions
            permissions="rug.field.reservations"
            render={() => (
              <FormInputDropdown
                multiple={false}
                name="availability"
                onChange={handleAvailability}
                options={options.onHold}
                search={false}
                text="Availability"
                value={availability}
              />
            )}
          />

          {(availability === 'available' || availability === 'customPeriod') && (
            <Permissions
              permissions="rug.field.reservations"
              render={() => (
                <div>
                  <Header
                    as="h5"
                    content={`Date Range
                  ${availability === 'available' ? ' (Optional)' : ''}
                `}
                    style={styles.header}
                  />
                  <Form.Group>
                    <Form.Field error={errors.dateReservation}>
                      <label>From</label>
                      <DatePicker
                        dateFormat="DD.MM.YYYY"
                        name="dateReservationMin"
                        onChange={handleDateReservationMin}
                        placeholderText="From (Required)"
                        selected={dateReservation.min ? moment(dateReservation.min) : ''}
                      />
                    </Form.Field>

                    <Form.Field>
                      <label>Until</label>
                      <DatePicker
                        dateFormat="DD.MM.YYYY"
                        name="dateReservationMax"
                        minDate={dateReservation.min ? moment(dateReservation.min) : ''}
                        onChange={handleDateReservationMax}
                        placeholderText="Until"
                        selected={dateReservation.max ? moment(dateReservation.max) : ''}
                      />
                    </Form.Field>
                  </Form.Group>
                </div>
              )}
            />
          )}

          {(availability === 'onHold' || availability === 'customPeriod' || availability === 'currentAndUpcoming') && (
            <Permissions
              permissions="rug.field.reservations"
              render={() => (
                <FormInputDropdown
                  name="reservationUser"
                  onChange={handleReservationUser}
                  options={reservationUserOptions}
                  text="Reserved or Updated by (Optional)"
                  value={reservationUser}
                />
              )}
            />
          )}

          <Permissions
            permissions="rug.field.reservations"
            render={() => (
              <FormInputDropdown
                name="reservationEvent"
                onChange={handleReservationEvent}
                options={reservationEventOptions}
                text="Event"
                value={reservationEvent}
              />
            )}
          />

          <Permissions
            permissions="rug.field.size"
            render={() => (
              <div>
                <Header as="h5" content="Size (qm)" style={styles.header} />
                <Form.Group>
                  <FormInputText
                    icon="expand"
                    name="sizeQmMin"
                    onChange={handleSizeQmMin}
                    text="from"
                    type="number"
                    value={sizeQm.min}
                    noLabel
                  />

                  <FormInputText
                    icon="expand"
                    name="sizeQmMax"
                    onChange={handleSizeQmMax}
                    text="to"
                    type="number"
                    value={sizeQm.max}
                    noLabel
                  />
                </Form.Group>
              </div>
            )}
          />

          <Permissions
            permissions="rug.field.dateReceipt"
            render={() => (
              <div>
                <Header as="h5" content="Receipt Year" style={styles.header} />
                <Form.Group>
                  <FormInputText
                    icon="add to calendar"
                    name="dateReceiptMin"
                    onChange={handleDateReceiptMin}
                    text="from"
                    value={dateReceipt.min}
                    noLabel
                  />

                  <FormInputText
                    icon="add to calendar"
                    name="dateReceiptMax"
                    onChange={handleDateReceiptMax}
                    text="to"
                    value={dateReceipt.max}
                    noLabel
                  />
                </Form.Group>
              </div>
            )}
          />

          <div style={styles.alignRight}>
            <Button content="Reset" icon="erase" onClick={handleReset} />
            <Button content="Apply" icon="checkmark" onClick={handleSubmit} />
          </div>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Button color="black" content="Close" icon="x" onClick={toggle} />
      </Modal.Actions>
    </Modal>
  )
}

ExtendedSearchModal.propTypes = {
  advisorOptions: PropTypes.array.isRequired,
  applyExtendedFilters: PropTypes.func.isRequired,
  applyFilters: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  extendedFilters: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  handleAdvisor: PropTypes.func.isRequired,
  handleAvailability: PropTypes.func.isRequired,
  handleDateReceiptMax: PropTypes.func.isRequired,
  handleDateReceiptMin: PropTypes.func.isRequired,
  handleDateReservationMax: PropTypes.func.isRequired,
  handleDateReservationMin: PropTypes.func.isRequired,
  handleDescription: PropTypes.func.isRequired,
  handleRequested: PropTypes.func.isRequired,
  handleReservationEvent: PropTypes.func.isRequired,
  handleReservationUser: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  handleSizeQmMax: PropTypes.func.isRequired,
  handleSizeQmMin: PropTypes.func.isRequired,
  handleSource: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  reservationEventOptions: PropTypes.array.isRequired,
  reservationUserOptions: PropTypes.array.isRequired,
  resetExtendedFilters: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
}

const composed = compose(
  Enhancer,
  Container,
)

export default composed(ExtendedSearchModal)
